import React, { useState, useEffect } from 'react';
import { Database, Download, Upload, Trash2, AlertTriangle, HardDrive, RefreshCw } from 'lucide-react';
import axios from 'axios';

interface BackupItem {
  filename: string;
  size_mb: number;
  created_at: string;
}

const Backup: React.FC = () => {
  const [backups, setBackups] = useState<BackupItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState<{ type: string; text: string } | null>(null);

  useEffect(() => { fetchBackups(); }, []);

  const fetchBackups = async () => {
    try {
      const response = await axios.get('https://api.lifeshare.app/api/v1/admin/backup/list');
      setBackups(response.data.backups || []);
    } catch (error) {
      console.error('Backup list error:', error);
    } finally {
      setLoading(false);
    }
  };

  const createBackup = async () => {
    setCreating(true);
    setMessage(null);
    try {
      await axios.post('https://api.lifeshare.app/api/v1/admin/backup/create');
      setMessage({ type: 'success', text: 'Backup başarıyla oluşturuldu!' });
      fetchBackups();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Backup oluşturulamadı' });
    } finally {
      setCreating(false);
    }
  };

  const restoreBackup = async (filename: string) => {
    if (!window.confirm(`⚠️ DİKKAT! Tüm mevcut veriler silinecek ve "${filename}" backup'ından geri yüklenecek. Emin misiniz?`)) return;
    try {
      await axios.post(`https://api.lifeshare.app/api/v1/admin/backup/restore/${filename}`);
      setMessage({ type: 'success', text: 'Backup başarıyla geri yüklendi! Sayfa yenilenecek...' });
      setTimeout(() => window.location.reload(), 2000);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Restore başarısız' });
    }
  };

  const deleteBackup = async (filename: string) => {
    if (!window.confirm(`"${filename}" silinecek. Emin misiniz?`)) return;
    try {
      await axios.delete(`https://api.lifeshare.app/api/v1/admin/backup/${filename}`);
      setMessage({ type: 'success', text: 'Backup silindi' });
      fetchBackups();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Silme başarısız' });
    }
  };

  const downloadBackup = async (filename: string) => {
    try {
      setMessage({ type: 'success', text: 'Dosya indiriliyor...' });
      const response = await axios.get(`https://api.lifeshare.app/api/v1/admin/backup/download/${filename}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setMessage({ type: 'success', text: `${filename} başarıyla indirildi!` });
    } catch (error: any) {
      setMessage({ type: 'error', text: 'Dosya indirilemedi' });
    }
  };

  const getTimeAgo = (timestamp: string) => {
    if (!timestamp) return '-';
    // Handle various timestamp formats
    let ts = timestamp;
    if (!ts.includes('Z') && !ts.includes('+')) {
      ts = ts + 'Z'; // Assume UTC if no timezone info
    }
    const now = Date.now();
    const time = new Date(ts).getTime();
    const diff = now - time;

    // If parsing failed or date is invalid
    if (isNaN(time)) return '-';

    // If in the future (clock skew), show as just now
    if (diff < 0) return 'Az önce';

    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (days > 0) return `${days} gün önce`;
    if (hours > 0) return `${hours} saat önce`;
    if (minutes > 0) return `${minutes} dk önce`;
    return 'Az önce';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="max-w-6xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">Backup & Restore</h1>
            <p className="text-sm text-gray-500 mt-1">Database yedekleme ve geri yükleme</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={createBackup} disabled={creating} className="px-4 py-2.5 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition-colors flex items-center gap-2">
              {creating ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <HardDrive className="w-4 h-4" />}
              <span className="hidden sm:inline">{creating ? 'Oluşturuluyor...' : 'Yeni Backup'}</span>
            </button>
            <button onClick={fetchBackups} className="p-2.5 bg-gray-200 text-gray-600 rounded-xl hover:bg-gray-300 transition-colors">
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Message */}
        {message && (
          <div className={`p-4 rounded-2xl text-sm ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
            {message.text}
          </div>
        )}

        {/* Warning */}
        <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-2xl p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">⚠️ Önemli Uyarılar</h3>
              <ul className="text-xs text-gray-600 space-y-0.5">
                <li>• Backup oluşturma 1-5 dakika sürebilir</li>
                <li>• Restore tüm verileri siler ve backup'tan yükler</li>
                <li>• Düzenli backup almak kritik önem taşır</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Backup Table */}
        <div className="bg-white rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">Mevcut Backup'lar ({backups.length})</h2>
          </div>

          {backups.length === 0 ? (
            <div className="p-12 text-center">
              <Database className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Henüz backup oluşturulmamış</p>
              <button onClick={createBackup} className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-xl text-sm font-medium hover:bg-purple-700">İlk Backup'ı Oluştur</button>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Dosya Adı</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Boyut</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Oluşturulma</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {backups.map((backup) => (
                      <tr key={backup.filename} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <Database className="w-5 h-5 text-green-600" />
                            <span className="text-sm font-medium text-gray-900">{backup.filename}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600">{backup.size_mb} MB</td>
                        <td className="px-4 py-4 text-sm text-gray-600">{getTimeAgo(backup.created_at)}</td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => downloadBackup(backup.filename)} className="p-2 text-green-600 hover:bg-green-50 rounded-xl transition" title="İndir"><Download className="w-5 h-5" /></button>
                            <button onClick={() => restoreBackup(backup.filename)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-xl transition" title="Restore"><Upload className="w-5 h-5" /></button>
                            <button onClick={() => deleteBackup(backup.filename)} className="p-2 text-red-600 hover:bg-red-50 rounded-xl transition" title="Sil"><Trash2 className="w-5 h-5" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden divide-y divide-gray-100">
                {backups.map((backup) => (
                  <div key={backup.filename} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Database className="w-4 h-4 text-green-600 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-900 truncate">{backup.filename}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span>{backup.size_mb} MB</span>
                          <span>•</span>
                          <span>{getTimeAgo(backup.created_at)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => downloadBackup(backup.filename)} className="p-2 text-green-600 hover:bg-green-50 rounded-xl" title="İndir"><Download className="w-4 h-4" /></button>
                        <button onClick={() => restoreBackup(backup.filename)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-xl" title="Restore"><Upload className="w-4 h-4" /></button>
                        <button onClick={() => deleteBackup(backup.filename)} className="p-2 text-red-600 hover:bg-red-50 rounded-xl" title="Sil"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Backup;
